import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation, Outlet, useNavigationType } from "react-router-dom";
import LeftSidePanel from "../components/conversationCategory/LeftSidePanel";
import useAxiosInstance from "../helpers/useAxiosInstance";
import { RefreshCcw, Ban, MailCheck, Link2 } from "lucide-react";
import { CustomConfirmDialog } from "../components/custom/CustomConfirmDialog";
import CustomCheckbox from "../components/custom/CustomCheckbox";
import useThreadStore from "../store/threadStore";
import { getDisplayName, getFirstName, getMessage } from "../utils/message.utils";
import { CustomMessageCarousel } from "../components/custom/CustomMessageCarousel";
import CustomRippleButton from "../components/custom/CustomRippleButton";
import CustomTextPopup from "../components/custom/CustomTextPopup";
import { messageCategory } from "../constants/message.catergory";
import { TourLinksModal } from "../components/custom/TourLinksModal";
import { CustomNotes } from "../components/custom/CustomNotes";
import { normalizePhone, normalizeUSPhone } from "../utils/phone.utils";

export default function ConversationCategory() {
  const [isThreadsLoading, setIsThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState(false);

  const [popupOpen, setPopupOpen] = useState(false);
  const [popupText, setPopupText] = useState("");
  const [popupPhone, setPopupPhone] = useState("");
  const [popupThreadId, setPopupThreadId] = useState("");
  const [popupClient, setPopupClient] = useState(null);

  //Tour Link
  const [isTourModalOpen, setIsTourModalOpen] = useState(false);
  const [tourModalThreadId, setTourModalThreadId] = useState(null);

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const listRef = useRef();
  const observer = useRef();
  const navigationType = useNavigationType();
  const isChatRoute = location.pathname.includes("/chats");

  const axiosInstance = useAxiosInstance();
  
  let clientNote = false
  if(params.category === "shared_units_notes" ){
    clientNote = true
  }

  const [clientNotesCheck, setClientNotesCheck] = useState([])

  const isCommissionsList = useMemo(() => params.category === messageCategory.renewalCheckInNoClient , [params.category]);
  const isClientNotesList = useMemo(() => params.category === messageCategory.sharedUnitsNotes , [params.category]);

  const {
    category,
    threads,
    cursor,
    hasMore,
    selectedThreadIds,
    allCategories,
    setCategory,
    catError,
    isCategoryLoading,
    categoryRefreshToken,
    setThreads,
    setThreadsOnError,
    addThreads,
    resetThreads,
    getSelectedThreads,
    setSelectedThreads,
    saveScrollPosition,
    getScrollPosition,
    removeThreadsById,
    removeCommissionsById,
    removeClientNotesById,
    decrementCategoryCount,
    refreshCategoryList,
  } = useThreadStore();

  useEffect(() => {
    refreshCategoryList(axiosInstance);
  }, []);

  // Automatically navigate to default category
  useEffect(() => {
    if (allCategories.length && !params.category && !isChatRoute) {
      navigate(`/conversations/${allCategories[0].name}`);
    }
  }, [allCategories, params.category]);

  // Fetch threads
  const fetchThreads = async (catName, cur = null, append = false) => {
    if (!catName || (append && (!hasMore || threadsError))) return;
    setIsThreadsLoading(!append);
    setThreadsError(false);
    try {
      const res = await axiosInstance.get(`/threads/list`, {
        params: { category: catName, cursor: cur, limit: 20 },
      });
      const newThreads = res.data.data ?? [];
      const nextCursor = res.data.pagination.nextCursor;
      const more = res.data.pagination.hasMore;
      if (append) addThreads(newThreads, nextCursor, more);
      else setThreads(newThreads, nextCursor, more);
    } catch (err) {
      console.error(err);
      if (!append) setThreadsOnError([], false);
      setThreadsError(true);
    } finally {
      setIsThreadsLoading(false);
    }
  };

  //UNMATCHED COMMISSIONS
  const fetchUnmatched = async (cur = null, append = false) => {
    if (append && (!hasMore || threadsError)) return;

    setIsThreadsLoading(!append);
    setThreadsError(false);

    try {
      const params = { limit: 15 };

      if (cur?.cursor_lease_end_date && cur?.cursor_commission_id) {
        params.cursor_lease_end_date = cur.cursor_lease_end_date;
        params.cursor_commission_id = cur.cursor_commission_id;
      }

      const res = await axiosInstance.get(`/commission/unmatched`, { params });

      const newRows = res.data.data ?? [];
      const nextCursor = res.data.pagination.nextCursor;
      const more = res.data.pagination.hasMore;

      append
        ? addThreads(newRows, nextCursor, more)
        : setThreads(newRows, nextCursor, more);

    } catch (err) {
      console.error(err);
      if (!append) setThreadsOnError([], false);
      setThreadsError(true);
    } finally {
      setIsThreadsLoading(false);
    }
  };

  // SHARED UNIT NOTES
  const fetchSharedNotes = async (cur = null, append = false) => {
    if (append && (!hasMore || threadsError)) return;

    setIsThreadsLoading(!append);
    setThreadsError(false);

    try {
      const params = { limit: 20 };

      // Composite cursor:
      if (cur?.cursor_created_at && cur?.cursor_id) {
        params.cursor_created_at = cur.cursor_created_at;
        params.cursor_id = cur.cursor_id;
      }

      const res = await axiosInstance.get(`/shared-units-notes/list`, { params });

      const newRows = res.data.data ?? [];
      const nextCursor = res.data.pagination.nextCursor;
      const more = res.data.pagination.hasMore;

      append
        ? addThreads(newRows, nextCursor, more)
        : setThreads(newRows, nextCursor, more);

    } catch (err) {
      console.error(err);
      if (!append) setThreadsOnError([], false);
      setThreadsError(true);
    } finally {
      setIsThreadsLoading(false);
    }
  };

  useEffect(() => {
    if (!params.category || isChatRoute) return;
    const selectedCategory = allCategories.find((c) => c.name === params.category);
    if (!selectedCategory) return;

    const scrollY = getScrollPosition(params.category) ?? 0;
    setCategory(selectedCategory);

    if (navigationType === "POP") {
      setTimeout(() => listRef.current?.scrollTo({ top: scrollY, behavior: "auto" }), 20);
    } else {
      resetThreads();

      if (params.category === messageCategory.renewalCheckInNoClient) {
        fetchUnmatched(null, false);
      }
      else if (params.category === messageCategory.sharedUnitsNotes) {
        fetchSharedNotes(null, false);
      }
      else {
        fetchThreads(params.category);
      }

      setTimeout(() => listRef.current?.scrollTo({ top: scrollY, behavior: "auto" }), 20);
    }
  }, [params.category, categoryRefreshToken, isChatRoute]);

  // Infinite scroll
  const lastThreadRef = useCallback(
    (node) => {
      if (isThreadsLoading || threadsError || !hasMore) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (params.category === messageCategory.renewalCheckInNoClient) {
            fetchUnmatched(cursor, true);
          }
          else if (params.category === messageCategory.sharedUnitsNotes) {
            fetchSharedNotes(cursor, true);
          }
          else {
            fetchThreads(params.category, cursor, true);
          }
        }
      });
      if (node) observer.current.observe(node);
    },
    [isThreadsLoading, threadsError, hasMore, cursor, params.category]
  );

  const handleIgnoreThread = async (threadId) => {
    try {
      CustomConfirmDialog({
        title: "Are you sure?",
        text: "Do you want to ignore this task?",
        icon: "warning",
        confirmText: "Yes, Ignore",
        cancelText: "Cancel",
        toastMessage: "Task ignored successfully!",
        toastError: "Failed to ignore the task",
        onConfirm: async () => {
          // try {
            const res = await axiosInstance.patch(`/threads/ignore`, { thread_id: threadId });
            if (res?.data?.result) {
              removeThreadsById([threadId]);
              decrementCategoryCount(params.category);
            }
          // } catch (err) {
          //   console.error("Ignore failed", err);
          // }
        },
      });
    }
    catch (err) {
      console.error("Failed to ignore the task:", err);
    }
  };

  const handleIgnoreClientNotes = async (id) => {
    try {
      CustomConfirmDialog({
        title: "Are you sure?",
        text: "Do you want to ignore this task?",
        icon: "warning",
        confirmText: "Yes, Ignore",
        cancelText: "Cancel",
        toastMessage: "Task ignored successfully!",
        toastError: "Failed to ignore the task",
        onConfirm: async () => {
            const res = await axiosInstance.patch(`/shared-units-notes/ignore`, { ids: [id] });
            if (res?.data?.result) {
              removeClientNotesById([id])
              decrementCategoryCount(params.category);
              setClientNotesCheck([])
            }
        },
      });
    }
    catch (err) {
      console.error("Failed to ignore the task:", err);
    }
  };

const handleNotified = async (
  threadId,
  phoneNumber, 
  category_name
) => {
    try {
      let displayText = ""

      if (messageCategory.renewalCheckIn == category_name) {
        displayText = "renewal"
      }
      else if (messageCategory.moveInCheckIn == category_name) {
        displayText = "move-in"
      }
      CustomConfirmDialog({
        title: "Are you sure?",
        text: `Do you want to mark this task as ${displayText} notified? It will remove the task from the category.`,
        icon: "warning",
        confirmText: "Yes",
        cancelText: "Cancel",
        toastMessage: "Task marked successfully!",
        toastError: "Failed to mark the task",
        onConfirm: async () => {
          //try {
            const res = await axiosInstance.post(`/commission/mark-notified`, {
              phone_number: phoneNumber,
              category_name: category_name
            });
            if (res?.data?.result) {
              removeThreadsById([threadId]);
              decrementCategoryCount(params.category);
            }
          // } catch (err) {
          //   console.error("Mark notified failed", err);
          // }
        },
      });
    }
    catch (err) {
      console.error("Failed to mark the task:", err);
    }
  };

const handleRenewalNotified = async (
  commission_id
) => {
    try {
      let displayText = "renewal"

      CustomConfirmDialog({
        title: "Are you sure?",
        text: `Do you want to mark this task as ${displayText} notified? It will remove the task from the category.`,
        icon: "warning",
        confirmText: "Yes",
        cancelText: "Cancel",
        toastMessage: "Task marked successfully!",
        toastError: "Failed to mark the task",
        onConfirm: async () => {
          //try {
            const res = await axiosInstance.post(`/commission/mark-renewal-notified`, {
              commission_id
            });
            if (res?.data?.result) {
              removeCommissionsById([commission_id]);
              decrementCategoryCount(params.category);
            }
          // } catch (err) {
          //   console.error("Mark notified failed", err);
          // }
        },
      });
    }
    catch (err) {
      console.error("Failed to mark the task:", err);
    }
  };

  const handleBulkIgnore = () => {
    try {
      const selected = getSelectedThreads();
      CustomConfirmDialog({
        title: "Are you sure?",
        text: `You are about to ignore ${clientNote ? clientNotesCheck.length : selected.length} tasks.`,
        icon: "warning",
        confirmText: "Yes, Ignore",
        cancelText: "Cancel",
        toastMessage: "Selected tasks ignored successfully!",
        toastError: "Failed to ignore selected tasks",
        onConfirm: async () => {
          if(clientNote){
            try {
              const res = await axiosInstance.patch(`/shared-units-notes/ignore`, { ids: clientNotesCheck });
            if (res?.data?.result) {
              removeClientNotesById(clientNotesCheck)
              decrementCategoryCount(params.category, clientNotesCheck.length);
              setClientNotesCheck([])
            }
              
            } catch (error) {
               console.error("Bulk ignore failed", error);
            }
            
          }else{
             try {
              const res = await axiosInstance.patch(`/threads/ignore/bulk`, {
                thread_ids: selected,
              });
              if (res?.data?.result) {
                removeThreadsById(selected);
                decrementCategoryCount(params.category, selected.length);
              }
            } catch (err) {
              console.error("Bulk ignore failed", err);
            }
          }
        },
      });
    } catch (err) {
      console.error("Failed to ignore selected tasks", err);
    }
  };

  const allVisibleIds = useMemo(() => threads.map((t) => t.thread_id), [threads]);
  const areAllVisibleSelected = allVisibleIds.every((id) => selectedThreadIds.has(id));
  const areSomeSelected = allVisibleIds.some((id) => selectedThreadIds.has(id));

  const toggleSelectAllVisible = (event) => {
    if (clientNote) {
      if (event.currentTarget.checked) {
        const allThreadIds = threads.map((thread) => thread.id);
        setClientNotesCheck(allThreadIds);
      } else {
        setClientNotesCheck([]);
      }
    }
    setSelectedThreads((prev) => {
      const updated = new Set(prev);
      if (areAllVisibleSelected) {
        // Unselect all visible
        allVisibleIds.forEach((id) => updated.delete(id));
      } else {
        // Select all visible
        allVisibleIds.forEach((id) => updated.add(id));
      }
      return updated;
    });
  };

  const saveScroll = () => {
    if (!params.category || !listRef.current || isChatRoute) return;
    saveScrollPosition(params.category, listRef.current.scrollTop);
  };

  return (
    <div className="flex h-[calc(100vh-64px)]">
      <LeftSidePanel
        categoryList={allCategories}
        isLoading={isCategoryLoading}
        onRefresh={() => refreshCategoryList(axiosInstance)}
        error={catError}
      />

      <main
        ref={listRef}
        onScroll={saveScroll}
        className={`flex-1 overflow-y-auto bg-gray-50 ${isChatRoute ? "p-1" : "p-6"}`}
      >
        {isChatRoute ? (
          <Outlet />
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold flex items-center space-x-2">
                {isCategoryLoading ? (
                  <>
                    <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
                    <div className="h-5 w-8 bg-gray-200 rounded animate-pulse" />
                  </>
                ) : (
                  <>
                    <span>{category?.label}</span>
                    {category?.count > 0 && (
                      <span className="text-base text-gray-500">
                        ({category.count})
                      </span>
                    )}
                  </>
                )}
              </h2>

              <div className="flex gap-2">
                {(selectedThreadIds.size > 0  || clientNotesCheck.length > 0 )&& (
                  <button
                    onClick={handleBulkIgnore}
                    className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
                    title="Ignore"
                  >
                    Ignore Selected ({ clientNote ? clientNotesCheck.length : selectedThreadIds.size})
                  </button>
                )}
                <button
                  className="flex items-center gap-1 px-2 py-1 border border-blue-500 text-blue-600 hover:text-white hover:bg-blue-500 rounded text-sm"
                  onClick={() => refreshCategoryList(axiosInstance)}
                  disabled={isThreadsLoading}
                  title="Refresh"
                >
                  <RefreshCcw size={16} />
                </button>
              </div>
            </div>

            <div className="rounded-md border overflow-hidden bg-white shadow-sm">
              {/* Header */}
              <div className="flex items-center bg-gray-100 px-4 py-3 font-semibold text-gray-700 text-sm border-b">
                <div className="w-1/12 flex items-center justify-start">
                { !(isCommissionsList) &&
                  <CustomCheckbox
                    disabled={!(threads?.length > 0)}
                    checked={areAllVisibleSelected}
                    indeterminate={!areAllVisibleSelected && areSomeSelected}
                    onChange={toggleSelectAllVisible}
                  />}
                </div>

                <div className="w-1/3">Client Name</div>
                <div className="w-1/2">{(isCommissionsList || isClientNotesList) ? "Details" : "Message Preview"}</div>
                <div className="w-1/3 text-center">Actions</div>
              </div>

              {/* Thread Rows */}
              {!(isThreadsLoading || isCategoryLoading) && threads.map((t, i) => {
                if (isCommissionsList) {
                  return (
                    <div
                      key={t.commission_id}
                      ref={i === threads.length - 1 ? lastThreadRef : null}
                      className="flex items-start px-4 py-4 border-b hover:bg-gray-50"
                    >
                      <div className="w-1/12"></div>

                      <div className="w-1/3 font-medium">
                        {t.commission_full_name}
                      </div>

                      <div className="w-1/2 text-gray-700">
                        <div>Property:{" "}
                          <strong>
                            {t.commission_property_name}
                          </strong>
                        </div>
                        <div>Lease ends:{" "}
                          <strong>
                            {new Date(t?.lease_end_date).toLocaleDateString("en-US")}
                          </strong>
                        </div>
                      </div>

                      <div className="w-1/3 text-center text-sm text-blue-600">
                        <button
                          onClick={() => handleRenewalNotified(t.commission_id)}
                          className="text-sm font-medium text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition px-2 py-1 rounded shadow-sm"
                          title="Mark Notified"
                        >
                          <MailCheck size={16} />
                        </button>
                      </div>
                    </div>
                  );
                }
                else if(isClientNotesList){
                  return (
                    <div
                      key={t.id}
                      ref={i === threads.length - 1 ? lastThreadRef : null}
                      className="flex items-start px-4 py-4 border-b hover:bg-gray-50"
                    >
                      <div className="w-1/12 flex items-center justify-start my-1">
                        <CustomCheckbox
                          checked={clientNotesCheck.includes(t.id)}
                          onChange={(event) => {
                            const isChecked = event.currentTarget.checked;

                            setClientNotesCheck((prev) => {
                              if (isChecked) {
                                return [...prev, t.id];
                              } else {
                                return prev.filter((id) => id !== t.id);
                              }
                            });
                          }}
                        />
                      </div>

                      <div className="w-1/3 font-medium">
                        {t?.shared_unit?.client?.threads?.at(0)?.thread_id ? (
                          <CustomRippleButton
                            tooltip="Open chat"
                            onClick={() => {
                              saveScroll();
                              navigate(
                                `/conversations/${params.category}/chats/${
                                  t?.shared_unit?.client?.threads?.at(0)
                                    ?.thread_id
                                }`
                              );
                            }}
                          >
                            {getDisplayName(t?.shared_unit?.client)}
                          </CustomRippleButton>
                        ) : (
                          getDisplayName(t?.shared_unit?.client)
                        )}
                      </div>

                      {/* Note Preview */}
                      <div className="w-1/2 text-gray-700 text-sm">
                        <CustomNotes
                          title_text={t?.shared_unit?.title_text}
                          unit_model_name={t?.shared_unit?.unit_model_text}
                          unit_info={t?.shared_unit?.sub_market_bd_ba_sqft_text}
                          notes={t?.shared_unit?.notes}
                          created_at={t?.created_at}
                        ></CustomNotes>
                      </div>

                      {/* Actions */}
                      <div className="w-1/3 flex justify-center gap-4">
                        <button
                          onClick={() => handleIgnoreClientNotes(t.id)}
                          className="text-sm font-medium text-red-500 hover:text-red-700 hover:bg-red-100 transition px-2 py-1 rounded shadow-sm"
                          title="Ignore"
                        >
                          <Ban size={16} />
                        </button>
                        <button
                          className="text-sm font-medium text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition px-2 py-1 rounded shadow-sm"
                          onClick={() => {
                            const encodedClientId = btoa(
                              encodeURIComponent(t?.shared_unit?.client?.id)
                            );
                            const utfUrl = `${window.location.origin}/shared-units?client=${encodedClientId}`;
                            window.open(
                              utfUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                        >
                          Client Portal
                        </button>
                        <button
                          className="text-sm font-medium text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition px-2 py-1 rounded shadow-sm"
                          onClick={() => {
                            setPopupText(
                              getMessage({
                                client_name: getFirstName(
                                  t?.shared_unit?.client ?? ""
                                ),
                              })
                            );
                            setPopupPhone(
                              normalizePhone(
                                t?.shared_unit?.client?.phone ?? ""
                              )
                            );
                            setPopupThreadId("");
                            setPopupClient(t?.shared_unit?.client);
                            setPopupOpen(true);
                          }}
                        >
                          Click to Text
                        </button>
                      </div>
                    </div>
                  );
                }
                return (<div
                  key={t.thread_id}
                  ref={i === threads.length - 1 ? lastThreadRef : null}
                  className="flex items-start px-4 py-4 border-b hover:bg-gray-50 transition-all duration-150"
                >
                  <div className="w-1/12 flex items-center justify-start my-1">
                    <CustomCheckbox
                      checked={selectedThreadIds.has(t.thread_id)}
                      onChange={() => {
                        setSelectedThreads((prev) => {
                          const updated = new Set(prev);
                          updated.has(t.thread_id)
                            ? updated.delete(t.thread_id)
                            : updated.add(t.thread_id);
                          return updated;
                        });
                      }}
                    />
                  </div>
                  <div
                    className="w-1/3 pr-4 cursor-pointer font-medium text-gray-800 hover:underline"
                  >
                    <CustomRippleButton
                      tooltip="Open chat"
                      onClick={() => {
                        saveScroll();
                        navigate(`/conversations/${params.category}/chats/${t.thread_id}`);
                      }}
                    >
                      {getDisplayName(t?.client, t?.participants ?? [], t?.contact)}
                    </CustomRippleButton>
                  </div>
                  {/* <div className="w-1/2 text-gray-600 text-sm leading-relaxed truncate pr-4">
                    <span className="italic text-gray-400">
                      Preview not implemented
                    </span> 
                  </div> */}
                  <div className="w-1/2 pr-4">
                  <CustomMessageCarousel messages={t?.messages} />
                    {/* {Array.isArray(t?.messages) && t.messages.length > 0 ? (
                      
                    ) : (
                      <span className="italic text-gray-400 text-sm">No messages</span>
                    )} */}
                  </div>

                  <div className="w-1/3 flex justify-center gap-4">
                    <button
                        onClick={() => {
                          setTourModalThreadId(t.thread_id)
                          setIsTourModalOpen(true)
                        }}
                        className="text-sm font-medium text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition px-2 py-1 rounded shadow-sm"
                        title="Link Toured Property"
                      >
                        <Link2 size={16} />
                    </button>
                    {[messageCategory.renewalCheckIn, messageCategory.moveInCheckIn].includes(t?.category_name) &&
                      <button
                        onClick={() => handleNotified(t.thread_id, t.phone_number, t?.category_name)}
                        className="text-sm font-medium text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition px-2 py-1 rounded shadow-sm"
                        title="Mark Notified"
                      >
                        <MailCheck size={16} />
                      </button>
                    }
                    <button
                      onClick={() => handleIgnoreThread(t.thread_id)}
                      className="text-sm font-medium text-red-500 hover:text-red-700 hover:bg-red-100 transition px-2 py-1 rounded shadow-sm"
                      title="Ignore"
                    >
                      <Ban size={16} />
                    </button>
                    <button
                      className="text-sm font-medium text-blue-500 hover:text-blue-700 hover:bg-blue-100 transition px-2 py-1 rounded shadow-sm"
                      onClick={() => {
                        setPopupText(
                          getMessage({
                            category_name: t?.category_name,
                            sub_category_name: t?.sub_category_name,
                            client_name: getFirstName(t?.client, t?.contact),
                            market: t?.market?.at(0) ?? "",
                            propertyName: t?.client?.commissions?.at(0)?.property_name ?? ""
                          }
                          )
                        );
                        setPopupPhone(t.participants[0])
                        setPopupThreadId(t.thread_id)
                        setPopupClient(t?.client)
                        setPopupOpen(true);
                      }}
                    >
                      Click to Text
                    </button>
                  </div>
                </div>)
                })}

              {/* Popup */}
              <CustomTextPopup
                isOpen={popupOpen}
                defaultText={popupText}
                phoneNumber={popupPhone}
                client={popupClient}
                onClose={() => {
                  setPopupOpen(false)
                  setPopupText("")
                  setPopupPhone("")
                  setPopupThreadId("")
                  setPopupClient(null)
                }}
                showSuggestion={
                  [
                    messageCategory.bookTour,
                    messageCategory.invoiceInfo,
                    messageCategory.leftOnRead,
                    messageCategory.unread
                  ].includes(params.category)
                }
                onSend={async ({ phone, message }) => {
                  console.log("Send this:", { phone, message });
                  window.open(`sms:${phone}?body=${message}`)
                  return
                }}
                onGenerateReply={async () => {
                  const response = await axiosInstance.get(`/classification/suggestions/${popupThreadId}`);

                  if (!response?.data?.result) {
                    // Bubble error up so ReplyPopup can display it
                    throw new Error(response?.data?.message);
                  }

                  // Return the array of suggestions
                  return response?.data?.data?.replies;

                }}
              />

              {/* Tour Links Modal */}
              <TourLinksModal
                threadId={tourModalThreadId}
                isOpen={isTourModalOpen}
                onClose={() => {
                  setTourModalThreadId(null)
                  setIsTourModalOpen(false)
                }}
                axiosInstance={axiosInstance}
              />

              {/* Threads: Loading / Error / End States */}
              {(isThreadsLoading || isCategoryLoading) &&
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-start px-4 py-4 border-b animate-pulse space-x-4"
                  >
                    <div className="w-1/3 h-4 bg-gray-200 rounded" />
                    <div className="w-1/2 h-4 bg-gray-200 rounded" />
                    <div className="w-1/6 h-4 bg-gray-200 rounded mx-auto" />
                  </div>
                ))}
              {threadsError && (
                <div className="px-4 py-6 text-center text-red-500">
                  Failed to load records.
                  <button
                    onClick={() => refreshCategoryList(axiosInstance)}
                    className="ml-4 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!isThreadsLoading && !threads.length && !threadsError && (
                <div className="text-center text-gray-500 py-6">
                  No records found.
                </div>
              )}
              {!hasMore && threads.length > 0 && !isThreadsLoading && (
                <div className="text-center text-gray-500 py-4">
                  No more records.
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
